import { z } from 'zod';
import { RARITIES, type Rarity } from '../rarity';

/**
 * The energy system of the ship: five components, nine attributes.
 *
 * ## What equipment is, and what it is not
 *
 * Equipment is the opposite of an artifact in exactly one respect, and that one
 * respect decides everything else. An artifact contributes the moment it is
 * OWNED; a component contributes only while it is EQUIPPED. So equipment has
 * slots, an equipped item cannot be listed or traded, and removing one takes
 * its numbers away at that instant.
 *
 * Everything else follows the rules the vault already has: an instance is an
 * `item_instances` row, its history is `inventory_events`, its trade is the
 * marketplace's, and destroying one retires the row rather than deleting it.
 *
 * ## Where the numbers live
 *
 * The ROLL is per instance and immutable (see `roll.ts`); this file holds only
 * what is true of every copy - which components exist, which attributes exist,
 * what unit each attribute is measured in, and the band a roll may fall in.
 * Nothing here is a float: every value is an exact integer in a fixed unit,
 * because a level-10 component multiplies its roll by 33.52212864 and a float
 * would have drifted long before that.
 */

/* -------------------------------------------------------------------------- */
/*  Components                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The five components, in the order the panel draws them.
 *
 * Ids are English and permanent; the labels are the game's own Portuguese and
 * are the only place those five names are written.
 */
export const EQUIPMENT_COMPONENTS = [
  'core',
  'chamber',
  'distributor',
  'conduits',
  'receptor',
] as const;
export type EquipmentComponent = (typeof EQUIPMENT_COMPONENTS)[number];
export const equipmentComponentSchema = z.enum(EQUIPMENT_COMPONENTS);

export const EQUIPMENT_COMPONENT_LABELS: Readonly<Record<EquipmentComponent, string>> =
  Object.freeze({
    core: 'Núcleo de Energia',
    chamber: 'Câmara de Contenção',
    distributor: 'Distribuidor de Energia',
    conduits: 'Condutores de Fluxo',
    receptor: 'Receptor de Energia',
  });

/** The loadout slot each component occupies. One instance per slot, ever. */
export const EQUIPMENT_SLOTS = [
  'equipment_core',
  'equipment_chamber',
  'equipment_distributor',
  'equipment_conduits',
  'equipment_receptor',
] as const;
export type EquipmentSlot = (typeof EQUIPMENT_SLOTS)[number];

export function slotOfComponent(component: EquipmentComponent): EquipmentSlot {
  return `equipment_${component}` as EquipmentSlot;
}

export function componentOfSlot(slot: string): EquipmentComponent | null {
  const component = slot.startsWith('equipment_') ? slot.slice('equipment_'.length) : null;
  return component && (EQUIPMENT_COMPONENTS as readonly string[]).includes(component)
    ? (component as EquipmentComponent)
    : null;
}

/* -------------------------------------------------------------------------- */
/*  Attributes                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The nine attributes a component may roll. No instance ever holds a duplicate.
 *
 * `bonusAttack` is deliberately first, and it is the one attribute that is not
 * a variant of another: it is added AFTER the percentage multiplier rather
 * than inside it, so it is worth the same to a player with 0% damage bonus and
 * to one with 900%. See `hunt/passive-attributes.ts` for where that ordering is
 * actually applied.
 */
export const EQUIPMENT_ATTRIBUTES = [
  'bonusAttack',
  'baseAttack',
  'attackPercent',
  'baseAttackSpeed',
  'attackSpeedPercent',
  'flatMaxHealth',
  'maxHealthPercent',
  'goldPerEnemy',
  'goldPercent',
] as const;
export type EquipmentAttribute = (typeof EQUIPMENT_ATTRIBUTES)[number];
export const equipmentAttributeSchema = z.enum(EQUIPMENT_ATTRIBUTES);

/**
 * The unit each attribute is stored and rolled in.
 *
 * Every one is an integer in a fixed sub-unit, chosen so a level-1 roll is
 * still expressible after being divided by nothing at all: the effective value
 * is derived from this integer by exact rational arithmetic and only becomes a
 * float at the very edge, where the physics needs one.
 */
export const EQUIPMENT_ATTRIBUTE_UNITS: Readonly<Record<EquipmentAttribute, string>> =
  Object.freeze({
    /** MILLI-damage: 1000 = +1 damage per projectile, applied after the percentage. */
    bonusAttack: 'milliDamage',
    /** MILLI-damage: 1000 = +1 base damage, inside the percentage. */
    baseAttack: 'milliDamage',
    /** Basis points: 10000 = +100%. */
    attackPercent: 'bps',
    /** MICRO-attacks per second: 1000000 = +1 attack/s. */
    baseAttackSpeed: 'microAttacksPerSecond',
    attackSpeedPercent: 'bps',
    /** MILLI-health: 1000 = +1 maximum health. */
    flatMaxHealth: 'milliHealth',
    maxHealthPercent: 'bps',
    /** MILLI-Gold per killed enemy: 1000 = +1 Gold. */
    goldPerEnemy: 'milliGold',
    goldPercent: 'bps',
  });

/** What the interface calls each attribute, in the language the game speaks. */
export const EQUIPMENT_ATTRIBUTE_LABELS: Readonly<Record<EquipmentAttribute, string>> =
  Object.freeze({
    bonusAttack: 'Ataque bônus',
    baseAttack: 'Ataque base',
    attackPercent: 'Ataque percentual',
    baseAttackSpeed: 'Velocidade de ataque base',
    attackSpeedPercent: 'Velocidade de ataque percentual',
    flatMaxHealth: 'Vida máxima fixa',
    maxHealthPercent: 'Vida máxima percentual',
    goldPerEnemy: 'Gold fixo por inimigo',
    goldPercent: 'Gold percentual',
  });

/* -------------------------------------------------------------------------- */
/*  Guaranteed identity                                                       */
/* -------------------------------------------------------------------------- */

/**
 * What each component IS, expressed as the group its first attribute is drawn
 * from.
 *
 * This is the whole reason a Núcleo is not a Receptor: the first roll is
 * constrained, the rest are free. A Núcleo Rare therefore always carries one
 * offensive attribute and one of anything; a Prismatic carries all nine, so the
 * guarantee is satisfied by construction rather than by a special case.
 */
export const EQUIPMENT_GUARANTEED_ATTRIBUTES: Readonly<
  Record<EquipmentComponent, readonly EquipmentAttribute[]>
> = Object.freeze({
  core: Object.freeze(['baseAttack', 'attackPercent', 'bonusAttack'] as EquipmentAttribute[]),
  chamber: Object.freeze(['flatMaxHealth', 'maxHealthPercent'] as EquipmentAttribute[]),
  distributor: Object.freeze(['baseAttackSpeed', 'attackSpeedPercent'] as EquipmentAttribute[]),
  conduits: Object.freeze([
    'bonusAttack',
    'baseAttackSpeed',
    'attackSpeedPercent',
  ] as EquipmentAttribute[]),
  receptor: Object.freeze(['goldPerEnemy', 'goldPercent'] as EquipmentAttribute[]),
});

/* -------------------------------------------------------------------------- */
/*  Rarity                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Equipment rarities: the nine tiers above Common.
 *
 * There is no Common component. The scale exists for the whole game (see
 * `rarity.ts`), and this is the SUBSET equipment actually occupies - stated
 * once here rather than assumed at each call site, exactly as `CannonRarity`
 * states the four tiers a cannon occupies.
 */
export const EQUIPMENT_RARITIES = RARITIES.filter((rarity) => rarity !== 'common');
export type EquipmentRarity = Exclude<Rarity, 'common'>;
export const equipmentRaritySchema = z.enum(
  EQUIPMENT_RARITIES as unknown as [EquipmentRarity, ...EquipmentRarity[]],
);

export function isEquipmentRarity(value: string): value is EquipmentRarity {
  return (EQUIPMENT_RARITIES as readonly string[]).includes(value);
}

/**
 * How many DISTINCT attributes a rarity carries. Exactly, never "at least".
 *
 * Rarity buys BREADTH, not magnitude: an Uncommon roll and a Prismatic roll of
 * the same attribute come out of the same band (see `ATTRIBUTE_ROLL_RANGES`).
 * A secret per-rarity value multiplier was considered and rejected - it would
 * make the same displayed attribute mean two different things, and the count
 * already produces a steep enough curve on its own, because a Prismatic
 * multiplies its cost, its Power and its whole contribution by having nine.
 */
export const ATTRIBUTE_COUNT_BY_RARITY: Readonly<Record<EquipmentRarity, number>> = Object.freeze({
  uncommon: 1,
  rare: 2,
  epic: 3,
  mythic: 4,
  legendary: 5,
  excellent: 6,
  perfect: 7,
  anomaly: 8,
  prismatic: 9,
});

export function attributeCountFor(rarity: EquipmentRarity): number {
  return ATTRIBUTE_COUNT_BY_RARITY[rarity];
}

/* -------------------------------------------------------------------------- */
/*  Roll bands                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Version of the ROLL TABLE below.
 *
 * Stored on every instance. An instance rolled under version 1 keeps its exact
 * values for ever - a future rebalance is a new version and new instances, not
 * a silent rewrite of what somebody already owns.
 */
export const EQUIPMENT_ROLL_VERSION = 2;

export interface AttributeRollRange {
  /** Inclusive lower bound of the level-1 roll, in the attribute's own unit. */
  readonly min: number;
  /** Inclusive upper bound. */
  readonly max: number;
}

/**
 * The band every level-1 roll falls in, PROVISIONAL and centralised.
 *
 * Chosen against the numbers the game actually runs on today, not invented:
 * the ship starts at 1 damage, 1 attack/second, 100 health and half a Gold per
 * normal kill (`HUNT_BALANCE`), and a point buys +1 damage, +0.02 attacks/s or
 * +10 health. So a level-1 roll is deliberately worth roughly one to a few
 * allocated points - visible immediately, and not a replacement for playing.
 *
 * The ceiling is what forced the bands down. Level 10 multiplies a roll by
 * 33.52212864, and five components can carry the same attribute at once, so a
 * band's top is really `max x 33.52 x 5` before anything else is counted. The
 * attack-speed percentage is the tightest of the nine for exactly that reason:
 * it multiplies a base that five other components are already raising, and the
 * product is what decides how many projectiles a second the arena has to draw.
 *
 * None of these is a design decision that has been taken - see
 * `docs/OPEN_QUESTIONS.md`.
 */
export const ATTRIBUTE_ROLL_RANGES: Readonly<Record<EquipmentAttribute, AttributeRollRange>> =
  Object.freeze({
    /** +0.30 .. +1.80 damage per projectile. Wider than `baseAttack` because
     * it never receives a percentage multiplier. */
    bonusAttack: Object.freeze({ min: 300, max: 1800 }),
    /** +0.20 .. +1.20 base damage, i.e. a fifth of a point to just over one. */
    baseAttack: Object.freeze({ min: 200, max: 1200 }),
    /** +1.5% .. +9% damage. */
    attackPercent: Object.freeze({ min: 150, max: 900 }),
    /** +0.010 .. +0.060 attacks/s: half an allocated point to three of them. */
    baseAttackSpeed: Object.freeze({ min: 10_000, max: 60_000 }),
    /** +0.5% .. +3% attack speed. The tightest band, see above. */
    attackSpeedPercent: Object.freeze({ min: 50, max: 300 }),
    /** +2 .. +12 maximum health: a fifth of a point to just over one. */
    flatMaxHealth: Object.freeze({ min: 2_000, max: 12_000 }),
    /** +1.5% .. +9% maximum health. */
    maxHealthPercent: Object.freeze({ min: 150, max: 900 }),
    /** +0.04 .. +0.24 Gold per enemy, against a base of 0.5 for a normal mob. */
    goldPerEnemy: Object.freeze({ min: 40, max: 240 }),
    /** +1.5% .. +9% Gold. */
    goldPercent: Object.freeze({ min: 150, max: 900 }),
  });

/** How many discrete values a roll may take. Used by the draw and by tests. */
export function rollOutcomeCount(attribute: EquipmentAttribute): number {
  const range = ATTRIBUTE_ROLL_RANGES[attribute];
  return range.max - range.min + 1;
}

/* -------------------------------------------------------------------------- */
/*  Levels                                                                    */
/* -------------------------------------------------------------------------- */

/** A component levels from 1 to 10, and never past it. */
export const EQUIPMENT_MAX_LEVEL = 10;

/* -------------------------------------------------------------------------- */
/*  Definitions                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The 45 definition ids: five components at nine rarities.
 *
 * The definition says what a component IS - which slot, which rarity, how many
 * attributes. What it ROLLED is on the instance, because two Prismatic Núcleos
 * are two different items and always will be.
 */
export function equipmentDefinitionId(
  component: EquipmentComponent,
  rarity: EquipmentRarity,
): string {
  return `equip_${component}_${rarity}`;
}

export interface EquipmentDefinitionEntry {
  readonly id: string;
  readonly component: EquipmentComponent;
  readonly rarity: EquipmentRarity;
  readonly attributeCount: number;
  /** Display name, in the game's own language. */
  readonly name: string;
  /** The inventory ICON, framed to the part's own bounds at 99%. */
  readonly assetKey: string;
  /**
   * The ALIGNED layer, framed to the whole assembly.
   *
   * A second crop of the SAME design, not a second design: it is what stacks
   * with the other four to draw the machine. Both come out of one render pass
   * in `pnpm assets:equipment`, which is why they can never disagree.
   */
  readonly layerAssetKey: string;
}

/** The manifest id of a component's inventory icon. */
export function equipmentIconAssetKey(
  component: EquipmentComponent,
  rarity: EquipmentRarity,
): string {
  return `equip_${component}_${rarity}`;
}

/** The manifest id of a component's aligned assembly layer. */
export function equipmentLayerAssetKey(
  component: EquipmentComponent,
  rarity: EquipmentRarity,
): string {
  return `equip_${component}_${rarity}_layer`;
}

export const EQUIPMENT_DEFINITION_ENTRIES: readonly EquipmentDefinitionEntry[] = Object.freeze(
  EQUIPMENT_COMPONENTS.flatMap((component) =>
    EQUIPMENT_RARITIES.map((rarity) =>
      Object.freeze({
        id: equipmentDefinitionId(component, rarity),
        component,
        rarity,
        attributeCount: ATTRIBUTE_COUNT_BY_RARITY[rarity],
        name: EQUIPMENT_COMPONENT_LABELS[component],
        assetKey: equipmentIconAssetKey(component, rarity),
        layerAssetKey: equipmentLayerAssetKey(component, rarity),
      }),
    ),
  ),
);

const ENTRIES_BY_ID: ReadonlyMap<string, EquipmentDefinitionEntry> = new Map(
  EQUIPMENT_DEFINITION_ENTRIES.map((entry) => [entry.id, entry]),
);

export function findEquipmentEntry(definitionId: string): EquipmentDefinitionEntry | null {
  return ENTRIES_BY_ID.get(definitionId) ?? null;
}

export function isEquipmentDefinitionId(definitionId: string): boolean {
  return ENTRIES_BY_ID.has(definitionId);
}

/* -------------------------------------------------------------------------- */
/*  The Quantum Stabiliser                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The consumable that turns a destroying failure into an ordinary one.
 *
 * Defined here so the vault, the upgrade path and the interface all name the
 * same id. It has NO drop in this delivery: nothing grants one yet, and
 * inventing a source would be inventing content.
 */
export const QUANTUM_STABILIZER_ID = 'quantum_stabilizer';
export const QUANTUM_STABILIZER_NAME = 'Estabilizador Quântico';
export const QUANTUM_STABILIZER_RARITY: Rarity = 'anomaly';
